/**
 * Os vínculos unidade → vaga que a PUBLICAÇÃO vai descartar — item 19 do lote 1 de 09/10/2026.
 *
 * O QUE ACONTECIA
 * Cada unidade guarda em `u.vagas` os ids das vagas avulsas vinculadas a ela. `paraBanco`
 * (mapeamento.ts) só consegue gravar em `app.unidade_vaga` o vínculo cujo id existe entre as
 * vagas do MESMO lado; o resto ele pula com um `continue` — "token sem vaga: o v67 também
 * ignora". Pular é a única coisa que ele pode fazer (não há vaga para apontar), mas pular
 * CALADO foi como uma versão do Verus saiu com zero vínculos sem ninguém ver: a importação da
 * planilha de vagas tinha trocado todos os ids, e os 34 vínculos ficaram apontando para o nada.
 *
 * O QUE ISTO FAZ
 * Conta, ANTES de publicar, quantos vínculos vão ficar de fora — pela MESMA regra de
 * `paraBanco`, sem mexer nele (a ida e volta dos goldens tem de seguir idêntica). A ponte de
 * dados avisa e publica sem eles. `tests/vinculos-orfaos.test.ts` prova que a conta daqui é
 * exatamente o que `paraBanco` descarta, lado a lado.
 *
 * A REGRA, LIDA DE `paraBanco`
 *  - o vínculo só é publicado se for TEXTO (`typeof token === 'string'`);
 *  - e se alguma vaga do MESMO lado (`vagasDem` para `unidadesDem`, `vagasDet` para
 *    `unidadesDet`) tiver exatamente esse id em texto. A chave do mapa de `paraBanco` é
 *    sempre `${lado} ${id original}` — tanto para o id antigo ("vg12") quanto para o UUID
 *    que veio do banco —, então "existe vaga com esse id" é a condição inteira.
 * Puro: não lê nem escreve fora do argumento.
 */
import type { EntradaMotor } from './mapeamento';

export interface VinculosOrfaos {
  /** vínculos das unidades do EL-154 (`unidadesDem`) sem vaga em `vagasDem` */
  dem: number;
  /** vínculos das unidades do EL-147 (`unidadesDet`) sem vaga em `vagasDet` */
  det: number;
}

const ehObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

export function vinculosOrfaos(entrada: EntradaMotor): VinculosOrfaos {
  const lev: Record<string, unknown> = ehObjeto(entrada) && ehObjeto(entrada.lev) ? entrada.lev as unknown as Record<string, unknown> : {};
  const conta = (unidadesK: string, vagasK: string): number => {
    const unidades = lev[unidadesK];
    if (!Array.isArray(unidades)) return 0;
    const vagas = lev[vagasK];
    const ids = new Set<string>();
    if (Array.isArray(vagas)) {
      for (const v of vagas) if (ehObjeto(v) && typeof v.id === 'string') ids.add(v.id);
    }
    let n = 0;
    for (const u of unidades) {
      if (!ehObjeto(u) || !Array.isArray(u.vagas)) continue;
      for (const token of u.vagas) if (typeof token !== 'string' || !ids.has(token)) n += 1;
    }
    return n;
  };
  return { dem: conta('unidadesDem', 'vagasDem'), det: conta('unidadesDet', 'vagasDet') };
}

/** "N vínculos de vaga …" — no singular quando é um só. */
function quantos(n: number): { vinculos: string; apontam: string; vagas: string } {
  return n === 1
    ? { vinculos: '1 vínculo de vaga', apontam: 'aponta', vagas: 'uma vaga que não existe mais' }
    : { vinculos: `${n} vínculos de vaga`, apontam: 'apontam', vagas: 'vagas que não existem mais' };
}

/*
 * Revisão do lote 1 (10/10/2026): os textos dizem EM QUAL TABELA estão os vínculos. São duas
 * colunas “Vaga Avulsa Vinculada” — a da “Demanda por unidade” e a do modal “Detalhamento de
 * Premissas Mensais” — e cada tabela mostra o seu próprio ⚠. Um número só, somado, mandava
 * refazer "na coluna" sem dizer qual; com órfãos só no Detalhamento, a tela à vista não tinha ⚠
 * nenhum. Nomes visíveis, sem ID (regra 6).
 */
const TABELA_DEM = '“Demanda por unidade”';
const TABELA_DET = '“Detalhamento de Premissas Mensais”';

/** Um lado só tem órfão → o nome da tabela com a preposição; os dois → `null`. */
function soUmLado(o: VinculosOrfaos): { na: string; da: string } | null {
  if (o.dem > 0 && o.det <= 0) return { na: `na ${TABELA_DEM}`, da: `da ${TABELA_DEM}` };
  if (o.det > 0 && o.dem <= 0) return { na: `no ${TABELA_DET}`, da: `do ${TABELA_DET}` };
  return null;
}
/** "34 na “Demanda por unidade” e 34 no “Detalhamento de Premissas Mensais”" */
const porTabela = (o: VinculosOrfaos): string => `${o.dem} na ${TABELA_DEM} e ${o.det} no ${TABELA_DET}`;

/** O aviso ANTES de publicar. `o.dem + o.det` > 0. */
export function avisoAntesDePublicar(o: VinculosOrfaos): string {
  const n = o.dem + o.det, q = quantos(n), um = soUmLado(o);
  const naoVai = `não ${n === 1 ? 'será publicado' : 'serão publicados'}`;
  return um
    ? `${q.vinculos} ${um.na} ${q.apontam} para ${q.vagas} e ${naoVai}.`
    : `${q.vinculos} ${q.apontam} para ${q.vagas} — ${porTabela(o)} — e ${naoVai}.`;
}

/**
 * O que se acrescenta ao "Publicado como versão N." quando houve descarte. O toast é um só e
 * o aviso de antes some quando o de sucesso chega — a informação não pode ir junto com ele.
 */
export function complementoDepoisDePublicar(o: VinculosOrfaos): string {
  const n = o.dem + o.det;
  if (n <= 0) return '';
  const q = quantos(n), um = soUmLado(o);
  const apontavam = n === 1 ? 'apontava' : 'apontavam';
  return um
    ? ` Sem ${n === 1 ? 'o' : 'os'} ${q.vinculos} ${um.da} que ${apontavam} para ${q.vagas}: `
      + 'refaça na coluna “Vaga Avulsa Vinculada” dessa tabela e publique de novo.'
    : ` Sem os ${q.vinculos} que ${apontavam} para ${q.vagas} (${porTabela(o)}): `
      + 'refaça na coluna “Vaga Avulsa Vinculada” dessas tabelas e publique de novo.';
}
